import { supabase } from './supabase'
import type { RealtimeChannel } from '@supabase/supabase-js'

// Shared presence channel instance
// This ensures all presence subscribers use the same channel
let presenceChannel: RealtimeChannel | null = null
let subscriberCount = 0

export interface PresenceUser {
  email: string
  joinedAt: string
}

type PresenceCallback = (users: PresenceUser[]) => void

const callbacks = new Set<PresenceCallback>()

function notifySubscribers(channel: RealtimeChannel) {
  const presenceState = channel.presenceState()
  const users: PresenceUser[] = []

  for (const key in presenceState) {
    const presences = presenceState[key]
    if (Array.isArray(presences) && presences.length > 0) {
      const presence = presences[0] as { email?: string; joinedAt?: string }
      if (presence.email) {
        users.push({
          email: presence.email,
          joinedAt: presence.joinedAt || new Date().toISOString(),
        })
      }
    }
  }

  users.sort((a, b) => a.email.localeCompare(b.email))
  callbacks.forEach((cb) => cb(users))
}

export function getPresenceChannel(): RealtimeChannel {
  if (!presenceChannel) {
    presenceChannel = supabase.channel('app-presence')

    presenceChannel.on('presence', { event: 'sync' }, () => {
      if (presenceChannel) {
        notifySubscribers(presenceChannel)
      }
    })

    presenceChannel.subscribe((status) => {
      console.log('[PresenceChannel] Status:', status)
    })
  }

  subscriberCount++
  return presenceChannel
}

export function releasePresenceChannel() {
  subscriberCount--
  if (subscriberCount <= 0 && presenceChannel) {
    supabase.removeChannel(presenceChannel)
    presenceChannel = null
    subscriberCount = 0
  }
}

export function subscribeToPresence(callback: PresenceCallback): () => void {
  callbacks.add(callback)

  // Immediately notify with current state if channel exists
  if (presenceChannel) {
    notifySubscribers(presenceChannel)
  }

  return () => {
    callbacks.delete(callback)
  }
}

export async function trackPresence(userId: string, email: string): Promise<void> {
  const channel = getPresenceChannel()

  // Wait for channel to be subscribed before tracking
  const state = channel.state
  if (state === 'joined') {
    await channel.track({
      email,
      joinedAt: new Date().toISOString(),
    })
  } else {
    // Channel not ready yet, wait for it
    return new Promise((resolve) => {
      const checkState = setInterval(async () => {
        if (channel.state === 'joined') {
          clearInterval(checkState)
          await channel.track({
            email,
            joinedAt: new Date().toISOString(),
          })
          resolve()
        }
      }, 100)

      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkState)
        resolve()
      }, 5000)
    })
  }
}

export async function untrackPresence(): Promise<void> {
  if (presenceChannel) {
    await presenceChannel.untrack()
  }
}
